// client/src/context/TenantContext.js
'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '@/variables';

const TenantContext = createContext({
    tenant: {
        brandName: 'VidyalayHelp',
        logoUrl: '/images/logo.png',
        faviconUrl: '/favicon.ico',
        theme: {
            primaryColor: '#0052cc',
            secondaryColor: '#0747a6',
            accentColor: '#ffab00',
            sidebarBg: '#ffffff'
        },
        supportEmail: 'support@vidyalayhelp.com',
        supportPhone: '+91 98765 43210',
        footerCopyright: `© ${new Date().getFullYear()} VidyalayHelp. All rights reserved.`,
        showPoweredBy: false,
        isPlatformDefault: true
    },
    loading: true
});

export const TenantProvider = ({ children }) => {
    const [tenant, setTenant] = useState({
        brandName: 'VidyalayHelp',
        logoUrl: '/images/logo.png',
        faviconUrl: '/favicon.ico',
        theme: {
            primaryColor: '#0052cc',
            secondaryColor: '#0747a6',
            accentColor: '#ffab00',
            sidebarBg: '#ffffff'
        },
        supportEmail: 'support@vidyalayhelp.com',
        supportPhone: '+91 98765 43210',
        footerCopyright: `© ${new Date().getFullYear()} VidyalayHelp. All rights reserved.`,
        showPoweredBy: false,
        isPlatformDefault: true
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTenant = async () => {
            try {
                if (typeof window === 'undefined') return;
                const host = window.location.hostname;

                const response = await axios.get(`${API_URL}/public/api/tenant/resolve`, {
                    params: { host },
                    // Public, cookie-less endpoint. axiosConfig turns withCredentials on
                    // globally, which makes browsers reject the server's `*` CORS header.
                    withCredentials: false
                });

                if (response.data?.success && response.data?.branding) {
                    const branding = response.data.branding;
                    setTenant(branding);

                    // Dynamically inject CSS variables for branding
                    if (branding.theme) {
                        const root = document.documentElement;
                        if (branding.theme.primaryColor) {
                            root.style.setProperty('--theme-primary', branding.theme.primaryColor);
                        }
                        if (branding.theme.secondaryColor) {
                            root.style.setProperty('--theme-secondary', branding.theme.secondaryColor);
                        }
                        if (branding.theme.accentColor) {
                            root.style.setProperty('--theme-accent', branding.theme.accentColor);
                        }
                        if (branding.theme.sidebarBg) {
                            root.style.setProperty('--theme-sidebar-bg', branding.theme.sidebarBg);
                        }
                    }

                    // Dynamically update document title if custom branding exists
                    if (!branding.isPlatformDefault && branding.brandName) {
                        document.title = branding.brandName;
                    }

                    // Dynamically update favicon if custom favicon exists
                    if (!branding.isPlatformDefault && branding.faviconUrl) {
                        let link = document.querySelector("link[rel*='icon']");
                        if (!link) {
                            link = document.createElement('link');
                            link.rel = 'shortcut icon';
                            document.getElementsByTagName('head')[0].appendChild(link);
                        }
                        link.href = branding.faviconUrl;
                    }
                }
            } catch (error) {
                console.warn('Failed to resolve tenant branding, using platform defaults', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTenant();
    }, []);

    return (
        <TenantContext.Provider value={{ tenant, loading }}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = () => useContext(TenantContext);

